import { useAuth } from '@commander/auth';
import { FEATURE_FLAGS, useFeatureFlag } from '@commander/feature-flags';
import { logger } from '@commander/logger';
import { useIsMobile } from '@commander/mobile-view';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Editor,
  Input,
  Label,
  LexicalViewer,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Separator,
  Switch,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  toast,
} from '@commander/ui';
import { useUserDetailsData } from '@commander/user-details';
import {
  RiAttachment2,
  RiCloseLine,
  RiDraftLine,
  RiFullscreenExitLine,
  RiFullscreenLine,
  RiInformationLine,
  RiMailLine,
  RiSendPlaneLine,
} from '@remixicon/react';
import DOMPurify from 'dompurify';
import { useEffect, useRef, useState } from 'react';
import { isSystemVariable } from '../../constants/systemVariables';
import {
  useDeleteEmailAttachment,
  useEmailSignature,
  useEmailSignatures,
  useEmailTemplate,
  useEmailTemplates,
  useQuoteSigningLink,
  useRenderEmailTemplate,
  useUploadEmailAttachment,
} from '../../hooks';
import type {
  Attachment,
  Email,
  EmailSignature,
  EmailTemplate,
  QuoteComposerContext,
  StructuredRecipient,
} from '../../types';
import { MAX_ATTACHMENT_BYTES, MAX_ATTACHMENT_LABEL } from '../../utils/attachments';
import { ensureSigningLink } from '../../utils/ensureSigningLink';
import { AiContentGenerator } from '../ai/AiContentGenerator';
import { ContactRecipientMultiSelect } from './ContactRecipientMultiSelect';
import { MobileEmailComposer } from './MobileEmailComposer';

/**
 * Deduplicates recipients by email address, preferring entries with tenantContactId.
 */
function deduplicateRecipients(
  recipients: StructuredRecipient[],
  excludeEmail?: string
): StructuredRecipient[] {
  const recipientMap = new Map<string, StructuredRecipient>();
  const excludeLower = excludeEmail?.toLowerCase();

  for (const r of recipients) {
    const emailLower = r.address.toLowerCase();
    if (emailLower === excludeLower) continue;

    const existing = recipientMap.get(emailLower);
    if (!existing || (r.tenantContactId && !existing.tenantContactId)) {
      recipientMap.set(emailLower, r);
    }
  }
  return Array.from(recipientMap.values());
}

/**
 * Builds the recipient list for a reply-all, deduplicating and excluding the current user.
 */
function buildReplyAllRecipients(email: Email, currentUserEmail: string): StructuredRecipient[] {
  const allRecipients: StructuredRecipient[] = [];

  if (email.direction === 'outgoing') {
    // User's original email - include all original "To" and "CC" recipients
    if (email.to?.length) allRecipients.push(...email.to);
    if (email.cc?.length) allRecipients.push(...email.cc);
  } else {
    // Incoming email - include the original sender
    allRecipients.push({ address: email.from, name: undefined, tenantContactId: undefined });
    if (email.to?.length) allRecipients.push(...email.to);
    if (email.cc?.length) allRecipients.push(...email.cc);
  }

  return deduplicateRecipients(allRecipients, currentUserEmail);
}

interface EmailComposerProps {
  isOpen: boolean;
  onClose: () => void;
  onSend: (emailData: any, draftId?: string) => Promise<void>;
  recipientEmail?: string;
  recipientName?: string;
  contactId?: string;
  replyToEmail?: Email; // Full email object for replies
  isReplyAll?: boolean; // Flag to indicate if this is a reply all
  template?: EmailTemplate;
  draftEmail?: Email; // Draft email to edit
  structuredRecipients?: StructuredRecipient[]; // Structured recipients with contact IDs
  disableRecipientEditing?: boolean; // Disable editing of the "to" field
  masterCompanyLocationId?: string; // Location ID to scope contact search
  initialSubject?: string; // Pre-filled subject line
  initialAttachments?: Attachment[]; // Pre-loaded attachments (already uploaded)
  initialBodyLexical?: any; // Pre-filled body as Lexical SerializedEditorState
  quoteContext?: QuoteComposerContext; // Set when composing a quote send
  autoGeneratePrompt?: string; // Auto-runs AI generation with this prompt and applies the result
  autoGenerateKey?: string; // Unique token for this auto-generate request, distinct from the prompt text
}

export function EmailComposer({
  isOpen,
  onClose,
  onSend,
  recipientEmail = '',
  recipientName = '',
  contactId,
  replyToEmail,
  isReplyAll = false,
  template,
  draftEmail,
  structuredRecipients,
  disableRecipientEditing = false,
  masterCompanyLocationId,
  initialSubject,
  initialAttachments,
  initialBodyLexical,
  quoteContext,
  autoGeneratePrompt,
  autoGenerateKey,
}: EmailComposerProps) {
  // Mobile detection - must be called before any conditional returns
  const isMobile = useIsMobile();

  // Recipient state using StructuredRecipient arrays for multi-select
  const [toRecipients, setToRecipients] = useState<StructuredRecipient[]>([]);
  const [ccRecipients, setCcRecipients] = useState<StructuredRecipient[]>([]);
  const [bccRecipients, setBccRecipients] = useState<StructuredRecipient[]>([]);
  const [subject, setSubject] = useState('');
  const [bodySerializedState, setBodySerializedState] = useState<any>(null);
  const [editorKey, setEditorKey] = useState<string>('new');
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<EmailTemplate | null>(template || null);
  const [selectedSignature, setSelectedSignature] = useState<EmailSignature | null>(null);
  const [selectedSignatureId, setSelectedSignatureId] = useState<string | null>(null);
  const [trackOpens, setTrackOpens] = useState(true);
  const [enrollInSequence, setEnrollInSequence] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showCc, setShowCc] = useState(false);
  const [showBcc, setShowBcc] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [isDraftMode, setIsDraftMode] = useState(false);
  const [isReply, setIsReply] = useState(false);

  // Inline image attachment IDs (images inserted via editor, not "Add Attachment" button)
  const [inlineImageIds, setInlineImageIds] = useState<string[]>([]);

  const { getSigningUrl, handleSigningUrlResolved } = useQuoteSigningLink(quoteContext);

  // Inline variable form state
  const [variableValues, setVariableValues] = useState<Record<string, string>>({});
  const [templateApplied, setTemplateApplied] = useState(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);

  // Template a loaded draft was composed from. Pre-selects the dropdown so the
  // draft shows which template produced it, while suppressing the fetch-and-apply
  // that a manual pick triggers — otherwise the draft's edited body would be
  // overwritten with the raw template.
  const draftTemplateIdRef = useRef<string | null>(null);

  // The recipient contact the applied body was personalized for. The template's
  // system variables render server-side from a single contact, so if the
  // recipient changes after apply the rendered body is stale for the new one.
  const appliedContactIdRef = useRef<string | null>(null);

  // AI email writer state
  const [isAiPopoverOpen, setIsAiPopoverOpen] = useState(false);

  // Get current user's email for filtering in reply all
  const { user } = useAuth();

  // Standing autopilot settings (admin-managed). When either is on, sequencing for this rep's
  // prospects is already handled automatically, so the per-send toggle is shown locked on
  // rather than as an off switch the rep could mistakenly think disables it:
  //  - first-send: a manual first email auto-enrolls into follow-ups.
  //  - classic autopilot: the prospect is sequenced by autopilot's own trigger; per-send
  //    enrollment would only conflict, so it's disabled and Autopilot owns it.
  const userDetails = useUserDetailsData();
  const firstSendAlwaysOn = userDetails?.autopilot_first_send_enabled === true;
  const classicAutopilotOn = userDetails?.auto_pilot_enabled === true;
  const sequenceLockedOn = firstSendAlwaysOn || classicAutopilotOn;

  // Feature flag for V2 features
  const isV2Enabled = useFeatureFlag(FEATURE_FLAGS.COMMS_V2);

  // Fetch templates list from API
  const { data: templatesData } = useEmailTemplates({ page: 1, limit: 100 });
  const templates = templatesData?.templates ?? [];

  // Fetch full template details when a template is selected
  const { data: fullTemplateData, isLoading: isLoadingTemplate } = useEmailTemplate(
    selectedTemplateId || '',
    { enabled: !!selectedTemplateId && selectedTemplateId !== draftTemplateIdRef.current }
  );

  // Server-side render on apply — resolves the system and location variables from
  // the recipient contact and merges custom variables.
  const renderTemplateMutation = useRenderEmailTemplate();

  // Fetch signatures list from API
  const { data: signaturesData } = useEmailSignatures({ page: 1, limit: 100 });
  const signatures = signaturesData?.signatures ?? [];

  // Fetch full signature details when a signature is selected
  const { data: fullSignatureData } = useEmailSignature(selectedSignatureId || '', {
    enabled: !!selectedSignatureId,
  });

  // Attachment mutations
  const uploadAttachmentMutation = useUploadEmailAttachment();
  const deleteAttachmentMutation = useDeleteEmailAttachment();
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Image upload handler - uploads to GCS and returns URL (like SignatureEditor)
  const handleImageUpload = async (file: File): Promise<string> => {
    try {
      // Upload with is_inline flag to mark as inline attachment
      const response = await uploadAttachmentMutation.mutateAsync({ file, is_inline: true });

      // Validate response has required fields
      if (!response || !response.id || !response.file_url) {
        throw new Error('Invalid response from server: missing required fields');
      }

      // Track attachment ID so we can link it when sending
      setInlineImageIds((prev) => [...prev, response.id]);

      // Return GCS URL to Editor (embedded in Lexical JSON)
      return response.file_url;
    } catch (error: any) {
      const errorMessage = error.message || 'Failed to upload image';
      toast.error(errorMessage);
      logger.error('Image upload failed:', error);
      throw new Error(errorMessage);
    }
  };

  // Preselect default signature when dialog opens
  useEffect(() => {
    if (isOpen && signatures.length > 0) {
      const defaultSignature = signatures.find((sig) => sig.isDefault);
      if (defaultSignature) {
        setSelectedSignatureId(defaultSignature.id);
      }
    }
  }, [isOpen, signatures]);

  useEffect(() => {
    // Prioritize structured recipients if provided
    if (structuredRecipients && structuredRecipients.length > 0) {
      setToRecipients(structuredRecipients);
    } else if (recipientEmail) {
      // Convert simple email to structured recipient
      setToRecipients([
        {
          address: recipientEmail,
          name: recipientName || undefined,
          tenantContactId: contactId,
        },
      ]);
    }
  }, [recipientEmail, recipientName, contactId, structuredRecipients]);

  // Pre-populate subject from prefill
  useEffect(() => {
    if (initialSubject) {
      setSubject(initialSubject);
    }
  }, [initialSubject]);

  // Pre-populate attachments from prefill
  useEffect(() => {
    if (initialAttachments && initialAttachments.length > 0) {
      setAttachments(initialAttachments);
    }
  }, [initialAttachments]);

  // Pre-populate body from prefill (Lexical JSON)
  useEffect(() => {
    if (initialBodyLexical) {
      setBodySerializedState(initialBodyLexical);
      setEditorKey(`prefill-${Date.now()}`);
    }
  }, [initialBodyLexical]);

  // Pre-populate form when editing a draft
  useEffect(() => {
    if (draftEmail) {
      setIsDraftMode(true);
      // Use structured recipients directly (already includes name and tenantContactId)
      setToRecipients(draftEmail.to);
      setCcRecipients(draftEmail.cc || []);
      setBccRecipients(draftEmail.bcc || []);
      setSubject(draftEmail.subject || '');
      setBodySerializedState(draftEmail.lexicalBody);
      setAttachments(draftEmail.metadata?.attachments || []);
      setTrackOpens(draftEmail.trackOpens ?? true);

      // Check if this draft is a reply
      if (draftEmail.inReplyTo) {
        setIsReply(true);
      }

      // Pre-select the template the draft was composed from so the dropdown
      // reflects it. The ref suppresses the fetch-and-apply a manual pick would
      // trigger, preserving the draft's existing body.
      setSelectedTemplate(null);
      setVariableValues({});
      if (draftEmail.templateId) {
        draftTemplateIdRef.current = draftEmail.templateId;
        setSelectedTemplateId(draftEmail.templateId);
        setTemplateApplied(true);
      } else {
        draftTemplateIdRef.current = null;
        setSelectedTemplateId(null);
        setTemplateApplied(false);
      }

      // Show CC/BCC fields if they have values
      if (draftEmail.cc && draftEmail.cc.length > 0) setShowCc(true);
      if (draftEmail.bcc && draftEmail.bcc.length > 0) setShowBcc(true);

      // Force editor re-render with draft content
      setEditorKey(`draft-${draftEmail.id}-${Date.now()}`);
    } else {
      setIsDraftMode(false);
      setIsReply(false);
    }
  }, [draftEmail]);

  useEffect(() => {
    if (template) {
      // Handle template passed as prop - trigger fetch if we only have minimal data
      if (!template.body || !template.variables) {
        setSelectedTemplateId(template.id);
      } else {
        // We already have full data
        setSelectedTemplate(template);
        setSubject(template.subject);
        setTemplateApplied(false);
        setBodySerializedState(template.body);
        setEditorKey(template.id);

        // Initialize variable values with empty strings
        const initialValues: Record<string, string> = {};
        for (const v of template.variables) {
          initialValues[v.key] = '';
        }
        setVariableValues(initialValues);
      }
    }
  }, [template]);

  useEffect(() => {
    if (!replyToEmail) return;

    setSubject(replyToEmail.subject || '');
    setIsReply(true);

    if (isReplyAll && user?.email) {
      setToRecipients(buildReplyAllRecipients(replyToEmail, user.email));
    } else if (replyToEmail.direction === 'outgoing') {
      // Reply to first original recipient
      if (replyToEmail.to?.length) {
        setToRecipients([replyToEmail.to[0]]);
      }
    } else {
      // Reply to sender
      setToRecipients([
        { address: replyToEmail.from, name: undefined, tenantContactId: undefined },
      ]);
    }
  }, [replyToEmail, isReplyAll, user?.email]);

  // When full template data loads, update the selected template and initialize variables
  useEffect(() => {
    if (fullTemplateData) {
      setSelectedTemplate(fullTemplateData);
      setSubject(fullTemplateData.subject);

      // Set the template body immediately (with handlebars placeholders)
      setBodySerializedState(fullTemplateData.body);
      setEditorKey(`${fullTemplateData.id}-${Date.now()}`); // Force remount with unique key

      // Initialize variable values with empty strings
      const initialValues: Record<string, string> = {};
      if (fullTemplateData.variables && fullTemplateData.variables.length > 0) {
        for (const v of fullTemplateData.variables) {
          initialValues[v.key] = '';
        }
      }
      setVariableValues(initialValues);
    }
  }, [fullTemplateData]);

  // When full signature data loads, update the selected signature
  useEffect(() => {
    if (fullSignatureData) {
      setSelectedSignature(fullSignatureData);
    }
  }, [fullSignatureData]);

  const handleTemplateSelect = (templateId: string) => {
    // A manual pick always fetches and applies, even when it matches the draft's
    // original template.
    draftTemplateIdRef.current = null;
    appliedContactIdRef.current = null;
    if (templateId === 'none') {
      setSelectedTemplate(null);
      setSelectedTemplateId(null);
      setSubject('');
      setBodySerializedState(null);
      setEditorKey('new');
      setVariableValues({});
      setTemplateApplied(false);
    } else {
      // Trigger fetch of full template details
      setSelectedTemplateId(templateId);
      setTemplateApplied(false);
    }
  };

  const handleSignatureSelect = (signatureId: string) => {
    if (signatureId === 'none') {
      setSelectedSignature(null);
      setSelectedSignatureId(null);
    } else {
      // Trigger fetch of full signature details (with Lexical body)
      setSelectedSignatureId(signatureId);
    }
  };

  const handleAttachment = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (file.size > MAX_ATTACHMENT_BYTES) {
      toast.error(`File too large. Max attachment size is ${MAX_ATTACHMENT_LABEL}.`);
      event.target.value = '';
      return;
    }

    try {
      // Upload file to backend
      const response = await uploadAttachmentMutation.mutateAsync({ file });

      // Add to attachments list with data from backend response
      const newAttachment: Attachment = {
        id: response.id,
        name: response.file_name,
        size: file.size,
        type: response.file_type,
        url: response.file_url,
        uploadedAt: new Date(response.created_at),
      };

      setAttachments([...attachments, newAttachment]);
      toast.success(`Uploaded ${file.name}`);

      // Reset input
      event.target.value = '';
    } catch (error: any) {
      toast.error(error.message || 'Failed to upload file');
      logger.error('Failed to upload attachment', error);
    }
  };

  const removeAttachment = async (id: string) => {
    try {
      await deleteAttachmentMutation.mutateAsync(id);
      setAttachments(attachments.filter((a) => a.id !== id));
      toast.success('Attachment removed');
    } catch (error: any) {
      toast.error(error.message || 'Failed to delete attachment');
      logger.error('Failed to delete attachment', error);
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  // Follow-up sequencing enrolls the recipient after a real first send, so it's
  // offered for a single known contact on a new (non-reply) email. The backend
  // only enrolls when that contact resolves to an assigned prospect.
  const canEnrollInSequence =
    toRecipients.length === 1 && Boolean(toRecipients[0]?.tenantContactId) && !isReply;

  const buildEmailData = (isDraft: boolean) => {
    return {
      to: toRecipients,
      cc: ccRecipients.length > 0 ? ccRecipients : undefined,
      bcc: bccRecipients.length > 0 ? bccRecipients : undefined,
      subject,
      body: bodySerializedState,
      attachmentIds: [...attachments.map((a) => a.id), ...inlineImageIds],
      // Fall back to selectedTemplateId so a draft opened for editing keeps its
      // template even when the full template was never fetched (display-only).
      templateId: selectedTemplate?.id ?? selectedTemplateId ?? undefined,
      signatureId: selectedSignature?.id,
      trackOpens,
      enrollInSequence: !isDraft && canEnrollInSequence ? enrollInSequence : undefined,
      replyToEmailId: replyToEmail?.id,
      isDraft,
    };
  };

  const handleSend = async () => {
    const emailData = buildEmailData(false);

    try {
      setIsSending(true);
      await onSend(emailData, isDraftMode ? draftEmail?.id : undefined);
      handleClose();
    } catch (error: any) {
      toast.error(error.message || 'Failed to send email');
      logger.error('Failed to send email', error);
    } finally {
      setIsSending(false);
    }
  };

  const handleSaveDraft = async () => {
    const draftData = buildEmailData(true);

    try {
      setIsSending(true);
      await onSend(draftData, isDraftMode ? draftEmail?.id : undefined);
      handleClose();
    } catch (error: any) {
      toast.error(error.message || 'Failed to save draft');
      logger.error('Failed to save draft', error);
    } finally {
      setIsSending(false);
    }
  };

  const handleClose = () => {
    // Reset form
    setToRecipients([]);
    setCcRecipients([]);
    setBccRecipients([]);
    setSubject('');
    setBodySerializedState(null);
    setEditorKey('new');
    setAttachments([]);
    setInlineImageIds([]); // Reset inline images
    setSelectedTemplate(null);
    setSelectedTemplateId(null);
    draftTemplateIdRef.current = null;
    appliedContactIdRef.current = null;
    setSelectedSignature(null);
    setSelectedSignatureId(null);
    setVariableValues({});
    setTemplateApplied(false);
    setShowCc(false);
    setShowBcc(false);
    setIsFullscreen(false);
    setIsReply(false);
    setEnrollInSequence(false);
    onClose();
  };

  // Custom (non-system) variables are the only ones collected by hand — the system
  // and location variables are resolved server-side from the recipient contact.
  const customVariables = (selectedTemplate?.variables ?? []).filter(
    (v) => !isSystemVariable(v.key)
  );

  // Apply the template by rendering it on the server: the system and location variables
  // resolve from the first recipient that maps to a known contact, and the custom
  // variables come from the inline form. This keeps the composer preview
  // byte-identical to what the recipient receives.
  const handleApplyTemplate = async () => {
    if (!selectedTemplate) return;

    const tenantContactId = toRecipients.find((r) => r.tenantContactId)?.tenantContactId ?? null;

    try {
      const { subject: renderedSubject, body: renderedBody } =
        await renderTemplateMutation.mutateAsync({
          templateId: selectedTemplate.id,
          tenantContactId,
          variables: variableValues,
        });

      setSubject(renderedSubject);
      setBodySerializedState(ensureSigningLink(renderedBody, getSigningUrl()));
      setEditorKey(`applied-${Date.now()}`);
      appliedContactIdRef.current = tenantContactId;
      setTemplateApplied(true);
    } catch (error: any) {
      toast.error(error.message || 'Failed to apply template');
      logger.error('Failed to apply template', error);
    }
  };

  // Re-arm the "Apply Template" step when the personalization recipient changes
  // after apply: the applied body was rendered for a specific contact, so a new
  // recipient would otherwise receive the previous one's name/company. Only the
  // rendered body is invalidated (the card re-appears); manual edits are kept.
  useEffect(() => {
    if (!templateApplied || !selectedTemplate) return;
    const currentContactId = toRecipients.find((r) => r.tenantContactId)?.tenantContactId ?? null;
    if (currentContactId !== appliedContactIdRef.current) {
      setTemplateApplied(false);
    }
  }, [toRecipients, templateApplied, selectedTemplate]);

  // Handle AI-generated content
  const handleAiContentGenerated = ({
    subject: generatedSubject,
    body,
  }: {
    subject?: string;
    body: any;
  }) => {
    // Set AI-generated body content
    setBodySerializedState(ensureSigningLink(body, getSigningUrl()));
    setEditorKey(`ai-generated-${Date.now()}`);

    // Set subject if it was generated
    if (generatedSubject) {
      setSubject(generatedSubject);
    }
  };

  // On mobile, render the mobile-optimized composer
  if (isMobile) {
    return (
      <MobileEmailComposer
        isOpen={isOpen}
        onClose={onClose}
        onSend={onSend}
        recipientEmail={recipientEmail}
        recipientName={recipientName}
        contactId={contactId}
        replyToEmail={replyToEmail}
        isReplyAll={isReplyAll}
        template={template}
        draftEmail={draftEmail}
        structuredRecipients={structuredRecipients}
        disableRecipientEditing={disableRecipientEditing}
        masterCompanyLocationId={masterCompanyLocationId}
        initialSubject={initialSubject}
        initialAttachments={initialAttachments}
        initialBodyLexical={initialBodyLexical}
        quoteContext={quoteContext}
      />
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent
        className={`${isFullscreen ? 'max-w-full h-screen m-0' : 'max-w-4xl max-h-[90vh]'} flex flex-col p-0`}
      >
        <DialogHeader className="px-6 py-4 border-b">
          <div className="flex items-center justify-between">
            <DialogTitle className="flex items-center gap-2">
              <RiMailLine className="h-5 w-5" />
              New Email
            </DialogTitle>
            <Button variant="ghost" size="icon" onClick={() => setIsFullscreen(!isFullscreen)}>
              {isFullscreen ? (
                <RiFullscreenExitLine className="h-4 w-4" />
              ) : (
                <RiFullscreenLine className="h-4 w-4" />
              )}
            </Button>
          </div>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto">
          <div className="px-6 py-4 space-y-4">
            {/* Recipients */}
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Label className="w-16 flex-shrink-0">To:</Label>
                <ContactRecipientMultiSelect
                  value={toRecipients}
                  onChange={setToRecipients}
                  placeholder="Search contacts or enter email..."
                  disabled={disableRecipientEditing}
                  masterCompanyLocationId={masterCompanyLocationId}
                />
                <div className="flex gap-1 flex-shrink-0">
                  <Button variant="ghost" size="sm" onClick={() => setShowCc(!showCc)}>
                    Cc
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => setShowBcc(!showBcc)}>
                    Bcc
                  </Button>
                </div>
              </div>

              {showCc && (
                <div className="flex items-center gap-2">
                  <Label className="w-16 flex-shrink-0">Cc:</Label>
                  <ContactRecipientMultiSelect
                    value={ccRecipients}
                    onChange={setCcRecipients}
                    placeholder="Search contacts or enter email..."
                    masterCompanyLocationId={masterCompanyLocationId}
                  />
                </div>
              )}

              {showBcc && (
                <div className="flex items-center gap-2">
                  <Label className="w-16 flex-shrink-0">Bcc:</Label>
                  <ContactRecipientMultiSelect
                    value={bccRecipients}
                    onChange={setBccRecipients}
                    placeholder="Search contacts or enter email..."
                    masterCompanyLocationId={masterCompanyLocationId}
                  />
                </div>
              )}
            </div>

            {/* Subject */}
            <div className="flex items-center gap-2">
              <Label htmlFor="subject" className="w-16">
                Subject:
              </Label>
              <Input
                id="subject"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder="Email subject"
                className="flex-1"
                disabled={isReply}
              />
            </div>

            {/* Template and Signature selectors */}
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <Label>Template:</Label>
                <Select value={selectedTemplateId || 'none'} onValueChange={handleTemplateSelect}>
                  <SelectTrigger className="w-48">
                    <SelectValue placeholder="Choose template" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    {templates.map((template) => (
                      <SelectItem key={template.id} value={template.id}>
                        {template.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex items-center gap-2">
                <Label>Signature:</Label>
                <Select value={selectedSignatureId || 'none'} onValueChange={handleSignatureSelect}>
                  <SelectTrigger className="w-48">
                    <SelectValue placeholder="Choose signature" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    {signatures.map((signature) => (
                      <SelectItem key={signature.id} value={signature.id}>
                        {signature.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Loading indicator for template fetch */}
            {selectedTemplateId && isLoadingTemplate && (
              <Card className="bg-muted/50">
                <CardContent className="py-4">
                  <p className="text-sm text-muted-foreground text-center">Loading template...</p>
                </CardContent>
              </Card>
            )}

            {/* Inline Variable Form. System variables (name, company, title, …)
                fill automatically from the recipient — only custom variables are
                collected here. */}
            {selectedTemplate && !templateApplied && (
              <Card className="bg-muted/50">
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm">
                    {customVariables.length > 0 ? 'Template Variables' : 'Apply Template'}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <p className="text-xs text-muted-foreground">
                    {customVariables.length > 0
                      ? 'Fill in the variables below and click "Apply Template". Name, company, title and email fill in automatically from the recipient.'
                      : 'Name, company, title and email fill in automatically from the recipient. Click "Apply Template" to insert it.'}
                  </p>
                  {customVariables.length > 0 && (
                    <div className="grid grid-cols-2 gap-3">
                      {customVariables.map((variable) => (
                        <div key={variable.key} className="space-y-1">
                          <Label htmlFor={`var-${variable.key}`} className="text-xs">
                            {variable.label}
                            {isV2Enabled && variable.required && (
                              <span className="text-destructive ml-1">*</span>
                            )}
                          </Label>
                          <Input
                            id={`var-${variable.key}`}
                            value={variableValues[variable.key] || ''}
                            onChange={(e) =>
                              setVariableValues({
                                ...variableValues,
                                [variable.key]: e.target.value,
                              })
                            }
                            placeholder={`Enter ${variable.label.toLowerCase()}`}
                            required={variable.required}
                          />
                        </div>
                      ))}
                    </div>
                  )}
                  <Button
                    onClick={handleApplyTemplate}
                    className="w-full"
                    size="sm"
                    disabled={renderTemplateMutation.isPending}
                  >
                    {renderTemplateMutation.isPending ? 'Applying…' : 'Apply Template'}
                  </Button>
                </CardContent>
              </Card>
            )}

            <Separator />

            {/* Email Body - Lexical Editor */}
            <div className="min-h-[300px] relative">
              {!selectedTemplateId || bodySerializedState !== null ? (
                <Editor
                  key={editorKey}
                  editorSerializedState={bodySerializedState || undefined}
                  onSerializedChange={setBodySerializedState}
                  onImageUpload={handleImageUpload}
                  height="min-h-[300px]"
                />
              ) : (
                <div className="flex items-center justify-center h-[300px]">
                  <div className="text-center">
                    <p className="text-sm text-muted-foreground">Loading template content...</p>
                  </div>
                </div>
              )}

              {/* AI Email Writer - Floating Button */}
              <div className="absolute bottom-4 right-4">
                <AiContentGenerator
                  type="email"
                  isOpen={isAiPopoverOpen}
                  onOpenChange={setIsAiPopoverOpen}
                  onContentGenerated={handleAiContentGenerated}
                  buttonClassName="shadow-lg"
                  contactId={contactId}
                  recipientName={recipientName}
                  masterCompanyLocationId={masterCompanyLocationId}
                  quoteId={quoteContext?.quoteId}
                  onSigningUrlResolved={handleSigningUrlResolved}
                  autoGeneratePrompt={autoGeneratePrompt}
                  autoGenerateKey={autoGenerateKey}
                />
              </div>
            </div>

            {/* Attachment Button */}
            <div className="flex items-center gap-2">
              <input
                ref={fileInputRef}
                type="file"
                onChange={handleAttachment}
                className="hidden"
                accept="*/*"
              />
              <Button
                variant="outline"
                size="sm"
                onClick={() => fileInputRef.current?.click()}
                type="button"
              >
                <RiAttachment2 className="h-4 w-4 mr-2" />
                Add Attachment
              </Button>
            </div>

            {/* Attachments */}
            {attachments.length > 0 && (
              <div className="space-y-2">
                <Label>Attachments:</Label>
                <div className="flex flex-wrap gap-2">
                  {attachments.map((attachment) => (
                    <Badge
                      key={attachment.id}
                      variant="secondary"
                      className="flex items-center gap-2 px-3 py-1"
                    >
                      <RiAttachment2 className="h-3 w-3" />
                      <span>{attachment.name}</span>
                      <span className="text-xs text-muted-foreground">
                        ({formatFileSize(attachment.size)})
                      </span>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-4 w-4 ml-1"
                        onClick={() => removeAttachment(attachment.id)}
                      >
                        <RiCloseLine className="h-3 w-3" />
                      </Button>
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            {/* Signature Preview */}
            {selectedSignature && (selectedSignature.lexicalBody || selectedSignature.htmlBody) && (
              <div className="p-3 bg-muted rounded-md">
                <Label className="text-xs text-muted-foreground mb-2 block">
                  Signature Preview:
                </Label>
                {selectedSignature.contentType === 'html' && selectedSignature.htmlBody ? (
                  <div
                    className="email-html-content"
                    style={{
                      fontSize: '13px',
                      fontFamily: 'Arial, sans-serif',
                      lineHeight: '1.5',
                    }}
                    // biome-ignore lint/security/noDangerouslySetInnerHtml: sanitized with DOMPurify
                    dangerouslySetInnerHTML={{
                      __html: DOMPurify.sanitize(selectedSignature.htmlBody),
                    }}
                  />
                ) : selectedSignature.lexicalBody ? (
                  <LexicalViewer
                    key={selectedSignature.id}
                    serializedState={selectedSignature.lexicalBody}
                  />
                ) : null}
              </div>
            )}

            <Separator />

            {/* Email Options */}
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Switch id="track-opens" checked={trackOpens} onCheckedChange={setTrackOpens} />
                <Label htmlFor="track-opens">Track Opens</Label>
              </div>
              {canEnrollInSequence && (
                <div className="flex items-center gap-2">
                  <Switch
                    id="enroll-in-sequence"
                    checked={sequenceLockedOn || enrollInSequence}
                    onCheckedChange={setEnrollInSequence}
                    disabled={sequenceLockedOn}
                  />
                  <Label htmlFor="enroll-in-sequence">Send automatic follow-ups</Label>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button
                          type="button"
                          className="text-muted-foreground hover:text-foreground"
                          aria-label="About automatic follow-ups"
                        >
                          <RiInformationLine className="h-4 w-4" />
                        </button>
                      </TooltipTrigger>
                      <TooltipContent className="max-w-xs">
                        If this is your first email to this prospect, follow-up emails are sent
                        automatically on days 7, 14, and 21 — and stop as soon as they reply.
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                  {firstSendAlwaysOn ? (
                    <span className="text-xs text-muted-foreground">On for your account</span>
                  ) : classicAutopilotOn ? (
                    <span className="text-xs text-muted-foreground">Managed by Autopilot</span>
                  ) : null}
                </div>
              )}
            </div>
          </div>
        </div>

        <DialogFooter className="px-6 py-4 border-t">
          <div className="flex items-center justify-between w-full">
            <Button variant="ghost" onClick={handleSaveDraft} disabled={isSending}>
              <RiDraftLine className="h-4 w-4 mr-2" />
              {isDraftMode ? 'Update Draft' : 'Save Draft'}
            </Button>
            <div className="flex items-center gap-2">
              <Button variant="outline" onClick={handleClose} disabled={isSending}>
                Cancel
              </Button>
              <Button
                onClick={handleSend}
                disabled={
                  toRecipients.length === 0 ||
                  !subject ||
                  isSending ||
                  (!!selectedTemplate && !templateApplied)
                }
              >
                <RiSendPlaneLine className="h-4 w-4 mr-2" />
                {isSending ? 'Sending...' : isDraftMode ? 'Send Email' : 'Send'}
              </Button>
            </div>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
